import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';

// UI Components
import Layout from '@/components/Layout';
import { Toaster } from 'sonner';
import { Toaster as ShadcnToaster } from '@/components/ui/toaster';
import ErrorBoundary from '@/components/ErrorBoundary';
import GlobalCelebration from '@/components/GlobalCelebration';

// Auth Pages
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';

// PWA components
import PWAInstallPrompt from '@/components/PWAInstallPrompt';

// Student Pages
import LearnPage from '@/pages/student/LearnPage';
import CuriosityCentre from '@/pages/student/CuriosityCentre';
import ContentDetailPage from '@/pages/student/ContentDetailPage';
import GeneralTopicsPage from '@/pages/student/GeneralTopicsPage';
import TopicContentPage from '@/pages/student/TopicContentPage';
import NewsDetailPage from '@/pages/student/NewsDetailPage';
import AllNewsPage from '@/pages/student/AllNewsPage';
import ChapterCuriosity from '@/curiosity/pages/ChapterCuriosity';
import VideoPlayer from '@/curiosity/pages/VideoPlayer';
import AudioPlayer from '@/curiosity/pages/AudioPlayer';
import TopicDetail from '@/curiosity/pages/TopicDetail';
import ContentDetail from '@/curiosity/pages/ContentDetail';
import ChatPage from '@/pages/student/ChatPage';
import ProfilePage from '@/pages/student/ProfilePage';
import LeaderboardPage from '@/pages/student/LeaderboardPage';
import ShopPage from '@/pages/student/ShopPage';
import CommunityPage from '@/pages/student/CommunityPage';
import SettingsPage from '@/pages/student/SettingsPage';
import GuardianCommunityPage from '@/pages/guardian/GuardianCommunityPage';

// Lazy Load Quest Pages
const QuestsPage = lazy(() => import('@/pages/student/QuestsPage'));
const PracticeModePage = lazy(() => import('@/pages/student/PracticeModePage'));
const TestModePage = lazy(() => import('@/pages/student/TestModePage'));
const ResultsPage = lazy(() => import('@/pages/student/ResultsPage'));

// Context Providers
import { QuestsProvider } from '@/context/QuestsContext';
import { SubjectChapterProvider } from '@/context/SubjectChapterContext';
import { TeacherProvider } from '@/context/TeacherContext';
import { PointsProvider } from '@/context/PointsContext';
import { PreferencesProvider } from '@/context/PreferencesContext';

// Teacher Pages
import TeacherDashboard from '@/pages/teacher/TeacherDashboard';
import TeacherAITools from '@/pages/teacher/TeacherAITools';
import TeacherTraining from '@/pages/teacher/TeacherTraining';
import TeacherCalendar from '@/pages/teacher/TeacherCalendar';
import TeacherMyClasses from '@/pages/teacher/TeacherMyClasses';
import TeacherStudentProfile from '@/pages/teacher/TeacherStudentProfile';
import TeacherLibrary from '@/pages/teacher/TeacherLibrary';
import TeacherParentUpdates from '@/pages/teacher/TeacherParentUpdates';
import TeacherNotes from '@/pages/teacher/TeacherNotes';
import TeacherProfile from '@/pages/teacher/TeacherProfile';
import TeacherCommunity from '@/pages/teacher/TeacherCommunity';
import TeacherCuriosityAnalytics from '@/pages/teacher/TeacherCuriosityAnalytics';
import QuestionGenerator from '@/pages/teacher/QuestionGenerator';
import ChapterPlannerList from '@/pages/teacher/ChapterPlannerList';
import ChapterTeachingPlanner from '@/pages/teacher/ChapterTeachingPlanner';

// Admin Pages
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminCommunityPage from '@/pages/admin/AdminCommunityPage';
import AdminStudents from '@/pages/admin/AdminStudents';
import AdminTeachers from '@/pages/admin/AdminTeachers';
import AdminClasses from '@/pages/admin/AdminClasses';
import AboutPage from '@/pages/AboutPage';

// Auth Utilities
import { hasAnySession, getActivePortal, getHomePagePath, isAuthenticated, getPortalType } from '@/utils/auth';

// Protected Route Component
function ProtectedRoute({ children }) {
  const portal = getPortalType(window.location.pathname || '');
  if (!isAuthenticated(portal)) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// Root Redirect Component
function RootRedirect() {
  if (hasAnySession()) {
    const activePortal = getActivePortal();
    if (activePortal) {
      return <Navigate to={getHomePagePath(activePortal)} replace />;
    }
  }
  return <Navigate to="/login" replace />;
}

// Login Route Component
function LoginRoute() {
  if (hasAnySession()) {
    const activePortal = getActivePortal();
    if (activePortal) {
      return <Navigate to={getHomePagePath(activePortal)} replace />;
    }
  }
  return <Login />;
}

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <PWAInstallPrompt />
        <ErrorBoundary>
          <SubjectChapterProvider>
            <TeacherProvider>
              <PreferencesProvider>
                <PointsProvider>
            <Routes>
                {/* Login route */}
          <Route path="/login" element={<LoginRoute />} />
          
          {/* Forgot Password route */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          
          {/* Root redirect - redirects based on active session */}
          <Route path="/" element={<RootRedirect />} />

          {/* Student Portal Routes - All under /student/ */}
          <Route path="/student" element={
            <ErrorBoundary>
              <ProtectedRoute>
                <Navigate to="/student/learn" replace />
              </ProtectedRoute>
            </ErrorBoundary>
          } />
          <Route path="/student/learn" element={
            <ProtectedRoute>
              <Layout activeSection="learn">
                <LearnPage />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Chat (student) */}
          <Route
            path="/student/chat"
            element={
              <ProtectedRoute>
                <Layout activeSection="chat">
                  <ChatPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Quests (Practice & Test) - Lazy loaded with Suspense */}
          <Route
            path="/student/quests"
            element={
              <ProtectedRoute>
                <Layout activeSection="quests">
                  <Suspense fallback={
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="w-10 h-10 border-4 border-[hsl(var(--green-bright))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-foreground">Loading Quests...</p>
                      </div>
                    </div>
                  }>
                    <QuestsPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/quests/:questId/practice"
            element={
              <ErrorBoundary>
                <ProtectedRoute>
                  <Layout activeSection="quests">
                    <Suspense fallback={
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="w-10 h-10 border-4 border-[hsl(var(--green-bright))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                          <p className="text-foreground">Loading Practice Mode...</p>
                        </div>
                      </div>
                    }>
                      <QuestsProvider>
                        <PracticeModePage />
                      </QuestsProvider>
                    </Suspense>
                  </Layout>
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path="/student/quests/:questId/test"
            element={
              <ErrorBoundary>
                <ProtectedRoute>
                  <Layout activeSection="quests">
                    <Suspense fallback={
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="w-10 h-10 border-4 border-[hsl(var(--green-bright))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                          <p className="text-foreground">Loading Test Mode...</p>
                        </div>
                      </div>
                    }>
                      <QuestsProvider>
                        <TestModePage />
                      </QuestsProvider>
                    </Suspense>
                  </Layout>
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path="/student/quests/:questId/results"
            element={
              <ErrorBoundary>
                <ProtectedRoute>
                  <Layout activeSection="quests">
                    <Suspense fallback={
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="w-10 h-10 border-4 border-[hsl(var(--green-bright))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                          <p className="text-foreground">Loading Results...</p>
                        </div>
                      </div>
                    }>
                      <QuestsProvider>
                        <ResultsPage />
                      </QuestsProvider>
                    </Suspense>
                  </Layout>
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route path="/student/curiosity" element={
            <ProtectedRoute>
              <Layout activeSection="curiosity">
                <CuriosityCentre />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/student/curiosity/topics" element={
            <ProtectedRoute>
              <Layout activeSection="curiosity">
                <GeneralTopicsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/student/curiosity/topics/:id" element={
            <ProtectedRoute>
              <Layout activeSection="curiosity">
                <TopicContentPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/student/curiosity/news" element={
            <ProtectedRoute>
              <Layout activeSection="curiosity">
                <AllNewsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/student/curiosity/news/:id" element={
            <ProtectedRoute>
              <Layout activeSection="curiosity">
                <NewsDetailPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/student/curiosity/chapter/:chapterId" element={
            <ProtectedRoute>
              <Layout activeSection="curiosity">
                <ChapterCuriosity />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/student/curiosity/video/:contentId" element={
            <ProtectedRoute>
              <Layout activeSection="curiosity">
                <VideoPlayer />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/student/curiosity/podcast/:contentId" element={
            <ProtectedRoute>
              <Layout activeSection="curiosity">
                <AudioPlayer />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/student/curiosity/topic/:topicId" element={
            <ProtectedRoute>
              <Layout activeSection="curiosity">
                <TopicDetail />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/student/curiosity/content/:contentId" element={
            <ProtectedRoute>
              <Layout activeSection="curiosity">
                <ContentDetail />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Profile Page (all roles) */}
          <Route
            path="/student/profile"
            element={
              <ProtectedRoute>
                <Layout activeSection="profile">
                  <ProfilePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Leaderboard Page */}
          <Route
            path="/student/achievements/leaderboard"
            element={
              <ProtectedRoute>
                <Layout activeSection="leaderboard">
                  <LeaderboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Shop Page */}
          <Route
            path="/student/achievements/shop"
            element={
              <ProtectedRoute>
                <Layout activeSection="shop">
                  <ShopPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Community Page */}
          <Route
            path="/student/community"
            element={
              <ProtectedRoute>
                <Layout activeSection="community">
                  <CommunityPage />
                </Layout>
              </ProtectedRoute>
            }
          />

            {/* Guardian Community Page */}
            <Route
              path="/guardian/community"
              element={
                <ProtectedRoute>
                  <Layout activeSection="community">
                    <GuardianCommunityPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/guardian/about"
              element={
                <ProtectedRoute>
                  <Layout activeSection="about">
                    <AboutPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

          {/* Settings Page */}
          <Route
            path="/student/settings"
            element={
              <ProtectedRoute>
                <Layout activeSection="settings">
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/about"
            element={
              <ProtectedRoute>
                <Layout activeSection="about">
                  <AboutPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Teacher Portal Routes - All under /teacher/ */}
          <Route path="/teacher" element={
            <ProtectedRoute>
              <Navigate to="/teacher/dashboard" replace />
            </ProtectedRoute>
          } />
          <Route
            path="/teacher/dashboard"
            element={
              <ProtectedRoute>
                <Layout activeSection="dashboard">
                  <TeacherDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
              path="/teacher/calendar"
            element={
              <ProtectedRoute>
                  <Layout activeSection="dashboard">
                    <TeacherCalendar />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
              path="/teacher/profile"
            element={
              <ProtectedRoute>
                  <Layout activeSection="profile">
                    <TeacherProfile />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/about"
            element={
              <ProtectedRoute>
                <Layout activeSection="about">
                  <AboutPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/ai-tools"
            element={
              <ProtectedRoute>
                <Layout activeSection="ai-tools">
                  <TeacherAITools />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/ai-tools/question-generator"
            element={
              <ProtectedRoute>
                <Layout activeSection="ai-tools">
                  <QuestionGenerator />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/ai-tools/chapter-planner"
            element={
              <ProtectedRoute>
                <Layout activeSection="ai-tools">
                  <ChapterPlannerList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/ai-tools/chapter-planner/create"
            element={
              <ProtectedRoute>
                <Layout activeSection="ai-tools">
                  <ChapterTeachingPlanner />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/ai-tools/chapter-planner/:planId"
            element={
              <ProtectedRoute>
                <Layout activeSection="ai-tools">
                  <ChapterTeachingPlanner />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/ai-tools/chapter-planner/:planId/periods/:periodNumber"
            element={
              <ProtectedRoute>
                <Layout activeSection="ai-tools">
                  <ChapterTeachingPlanner />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/training"
            element={
              <ProtectedRoute>
                <Layout activeSection="training">
                  <TeacherTraining />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
              path="/teacher/notes"
            element={
              <ProtectedRoute>
                  <Layout activeSection="notes">
                    <TeacherNotes />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/curiosity-analytics"
            element={
              <ProtectedRoute>
                <Layout activeSection="curiosity-analytics">
                  <TeacherCuriosityAnalytics />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/parent-updates"
            element={
              <ProtectedRoute>
                <Layout activeSection="parent-updates">
                  <TeacherParentUpdates />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/community"
            element={
              <ProtectedRoute>
                <Layout activeSection="community">
                  <TeacherCommunity />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/my-classes"
            element={
              <ProtectedRoute>
                <Layout activeSection="dashboard">
                  <TeacherMyClasses />
                </Layout>
              </ProtectedRoute>
            }
            />
            <Route
              path="/teacher/student/:studentId"
              element={
                <ProtectedRoute>
                  <Layout activeSection="student-profile">
                    <TeacherStudentProfile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher/library"
              element={
                <ProtectedRoute>
                  <Layout activeSection="library">
                    <TeacherLibrary />
                  </Layout>
                </ProtectedRoute>
              }
            />
          {/* Admin Portal Routes - All under /admin/ */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <Navigate to="/admin/dashboard" replace />
            </ProtectedRoute>
          } />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <Layout activeSection="dashboard">
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/profile"
            element={
              <ProtectedRoute>
                <Layout activeSection="profile">
                  <ProfilePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/about"
            element={
              <ProtectedRoute>
                <Layout activeSection="about">
                  <AboutPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/students"
            element={
              <ProtectedRoute>
                <Layout activeSection="students">
                  <AdminStudents />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/teachers"
            element={
              <ProtectedRoute>
                <Layout activeSection="teachers">
                  <AdminTeachers />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/classes"
            element={
              <ProtectedRoute>
                <Layout activeSection="classes">
                  <AdminClasses />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/lessons"
            element={
              <ProtectedRoute>
                <Layout activeSection="lessons">
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/assessments"
            element={
              <ProtectedRoute>
                <Layout activeSection="assessments">
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics/school"
            element={
              <ProtectedRoute>
                <Layout activeSection="school-analytics">
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics/students"
            element={
              <ProtectedRoute>
                <Layout activeSection="student-analytics">
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics/teachers"
            element={
              <ProtectedRoute>
                <Layout activeSection="teacher-analytics">
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute>
                <Layout activeSection="settings">
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
            />
            <Route
              path="/admin/community"
              element={
                <ProtectedRoute>
                  <Layout activeSection="community">
                    <AdminCommunityPage />
                  </Layout>
                </ProtectedRoute>
              }
          />
            </Routes>
                <GlobalCelebration />
                </PointsProvider>
              </PreferencesProvider>
            </TeacherProvider>
          </SubjectChapterProvider>
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster />
      <ShadcnToaster />
    </div>
  );
}

export default App;
